/* eslint-disable indent */
import './style.scss';

import { useMemo, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import Pagination from 'components/Pagination';
import ErrorDialog from 'components/ErrorDialog';
import { AbilityContext, Can } from 'components/Can';
import { getAllByUser as getAllOrganizationsByUser } from 'apis/organizations.api';
import { getAllLiquidation } from 'apis/liquidation.api';
import { exportExcel } from 'apis/projects.api';
import { handleEnterKeyPress, mapError, sanitizeObject } from 'utils/func';
import useSelectionProject from 'utils/hooks/useSelectionProject';
import { totalExpenditureSchema } from 'features/ReviseProjectProposal/validation';
import SearchFilters from 'features/CompletedProjectProposal/components/SearchFilters';
import { PROJECT_NEW_STATUSES, PROJECT_STATUSES } from 'constant';
import { statusColors } from 'utils/func/dashboard';

function CompletedProjectProposalViewList() {
  // #region data
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams({ replace: true });
  const ability = useContext(AbilityContext);

  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);
  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _ownerName: { value: null },
    _organization: { value: null },
    _totalExpenditure: { value: null },
    _status: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: null,
    ownerName: null,
    organizationId: null,
    totalExpenditure: { min: null, max: null },
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: null,
    ownerName: null,
    organizationId: null,
    totalExpenditure: { min: null, max: null },
  });

  const {
    data: _data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['projectsSubmitted', pagination, appliedFilters],
    queryFn: () => {
      const queries = {
        ...appliedFilters,
        projectStatus: [PROJECT_NEW_STATUSES.ACCEPTANCE_QUALIFIED_PROJECT],
      };

      return getAllLiquidation(pagination.first, pagination.rowsPerPage, sanitizeObject(queries));
    },
    enabled: isLoadedFilterFromLocalStorage,
  });

  const {
    mutate: mutateExportExcel,
    isLoading: isExportExcelLoading,
    isError: isExportExcelError,
    error: exportExcelError,
  } = useMutation(exportExcel);

  const { data: organizationsData } = useQuery(
    ['propjectProposalOrganizations'],
    getAllOrganizationsByUser
  );

  const organizations = useMemo(
    () =>
      organizationsData?.data?.map((e) => ({
        id: e?.id,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const dataWithProjectProposalId = useMemo(
    () =>
      (data?.data ?? []).map((item) => ({
        ...item,
        projectProposalId: item?.projectProposalId ?? item?.projectProposal?.id,
      })),
    [data]
  );

  const selectionData = useMemo(
    () => ({
      ...data,
      data: dataWithProjectProposalId,
    }),
    [data, dataWithProjectProposalId]
  );

  const {
    selectedItems,
    unselectedItems,
    isSelectedAll,
    isUnselectedSome,
    handleAllRowSelect,
    handleAllRowUnselect,
    handleRowSelect,
    handleRowUnselect,
  } = useSelectionProject(selectionData, pagination);
  // #endregion Data

  const handleExportExcel = () => {
    mutateExportExcel(
      {
        selectedItems: selectedItems.map((e) => e.projectProposalId),
        unselectedItems: unselectedItems.map((e) => e.projectProposalId),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
        projectStatus: [PROJECT_NEW_STATUSES.ACCEPTANCE_QUALIFIED_PROJECT],
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = t('project.excelProjectCompleted');
            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  const formatName = (value) => (
    <Link to={`/projects-completed/detail/liquidation/${value.id}`} className="hover:underline">
      {value?.projectProposal?.vietnameseName}
    </Link>
  );

  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.name')}
      className="p-column-filter"
      onKeyDown={(e) => {
        handleEnterKeyPress(e, () => {
          setAppliedFilters(filters);
        });
      }}
    />
  );

  const formatOwner = (value) => value?.projectProposal?.projectProposalContacts[0]?.fullname;

  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.owner')}
      className="p-column-filter"
    />
  );

  const formatOrganization = (value) =>
    t('lang') === 'vi'
      ? value?.projectProposal?.projectProposalOrganizations[0]?.name
      : value?.projectProposal?.projectProposalOrganizations[0]?.englishName;

  const organizationFilter = () => (
    <MultiSelect
      value={filters.organizationId}
      onChange={(e) => {
        const newFilters = { ...filters, organizationId: e.value };
        setFilters(newFilters);
      }}
      options={organizations}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.organization')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-GB') : '');

  const formatStartDate = (value) => formatDate(value?.startDate);

  const formatEndDate = (value) => formatDate(value?.endDate);

  const formatTotalExpenditure = (value) => {
    if (value?.totalExpenditure === 0 && value?.proposedExpenditureSummaries?.length > 0) {
      const total = value?.proposedExpenditureSummaries.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0);
      return total?.toLocaleString('vi');
    }
    return value?.totalExpenditure?.toLocaleString('vi') || 0;
  };

  const totalExpenditureFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-3">{t('projectProposal.viewList.from')}</span>
        <InputNumber
          value={filters?.totalExpenditure?.min ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              totalExpenditure: { ...filters.totalExpenditure, min: e.value },
            };
            setFilters(newFilters);
          }}
          className={classNames('currency', {
            'p-invalid': !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            }),
          })}
          tooltip={
            !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            })
              ? t('projectProposal.viewList.validation.min')
              : ''
          }
          tooltipOptions={{ position: 'bottom' }}
          mode="currency"
          currency="VND"
          locale="vi-VN"
          useGrouping
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-3">{t('projectProposal.viewList.to')}</span>
        <InputNumber
          value={filters?.totalExpenditure?.max ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              totalExpenditure: { ...filters.totalExpenditure, max: e.value },
            };
            setFilters(newFilters);
          }}
          className={classNames('currency', {
            'p-invalid': !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            }),
          })}
          tooltip={
            !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            })
              ? t('projectProposal.viewList.validation.max')
              : ''
          }
          tooltipOptions={{ position: 'bottom' }}
          mode="currency"
          currency="VND"
          locale="vi-VN"
          useGrouping
        />
      </div>
    </div>
  );

  const formatStatus = (value) => {
    const status = value.dashboardStatus;

    return (
      <Tag
        style={{
          maxWidth: '8rem',
          backgroundColor: statusColors.submit[status],
          color: '#ffffff',
        }}
        value={t(`projectNewStatuses.${status}`)}
        rounded
      />
    );
  };

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      disabled={
        !totalExpenditureSchema.isValidSync({
          min: filters?.totalExpenditure?.min ?? undefined,
          max: filters?.totalExpenditure?.max ?? undefined,
        })
      }
      severity="success"
    />
  );

  const renderHeader = () => (
    <div className="flex justify-content-between">
      <Button
        type="button"
        icon="pi pi-filter"
        label={t('projectProposal.viewList.searchFilters')}
        outlined
        onClick={() => setIsVisibleSearchFilters(true)}
      />
    </div>
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    ); // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    setFilters({
      ...localFilters,
      projectStatus: [PROJECT_STATUSES.COMPLETED],
      totalExpenditure: { min: undefined, max: undefined },
    });
    setAppliedFilters({
      ...localFilters,
      projectStatus: [PROJECT_STATUSES.COMPLETED],
      totalExpenditure: { min: undefined, max: undefined },
    });
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
    setIsLoadedFilterFromLocalStorage(true);
  }, []);

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex align-items-center justify-content-between gap-2 pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('projectProposal.viewList.title')}
        </h4>
        <div className="flex gap-2">
          <Can do="export_excel" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('projectProposal.viewList.tooltip.excel')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isExportExcelLoading },
                  { 'pi-file-excel': !isExportExcelLoading }
                )}
                severity="success"
                onClick={handleExportExcel}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isExportExcelLoading
                }
              />
            </div>
          </Can>
        </div>
      </div>

      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          organizations={organizations}
        />
      )}

      <DataTable
        value={dataWithProjectProposalId}
        className="project-list"
        dataKey="projectProposalId"
        showGridlines
        scrollable
        loading={isLoading}
        header={renderHeader}
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        selection={selectedItems}
        selectionMode="multiple"
        showSelectAll={false}
        onRowSelect={handleRowSelect}
        onRowUnselect={handleRowUnselect}
      >
        {ability.can('export_excel', 'project_proposal') ? (
          <Column
            selectionMode="multiple"
            header={
              <Checkbox
                checked={isSelectedAll}
                disabled={isLoading || !dataWithProjectProposalId?.length}
                onChange={() => (!isSelectedAll ? handleAllRowSelect() : handleAllRowUnselect())}
              />
            }
            headerStyle={{ width: '3rem' }}
            frozen
          />
        ) : null}
        <Column
          header={t('projectProposal.viewList.name')}
          headerStyle={filterActiveStyle('vietnameseName')}
          body={formatName}
          style={{ minWidth: '10rem', maxWidth: '16rem', textAlign: 'left', color: '#6366F1' }}
          frozen
          alignFrozen="left"
          filter
          filterField="_vietnameseName"
          filterElement={vietnameseNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, vietnameseName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
        />
        <Column
          header={t('projectProposal.viewList.owner')}
          headerStyle={{ ...filterActiveStyle('ownerName'), justifyContent: 'center' }}
          body={formatOwner}
          style={{ minWidth: '8rem', textAlign: 'left' }}
          filter
          filterField="_ownerName"
          filterElement={ownerNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, ownerName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
        />
        <Column
          header={t('projectProposal.viewList.organization')}
          headerStyle={{ ...filterActiveStyle('organizationId'), justifyContent: 'center' }}
          body={formatOrganization}
          style={{ minWidth: '8rem', textAlign: 'left' }}
          filter
          filterField="_organization"
          filterElement={organizationFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu={organizations?.length > 1}
          onFilterClear={() => {
            const newFilters = { ...filters, organizationId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
        />
        <Column
          header={t('projectProposal.viewList.totalExpenditure')}
          headerStyle={{ ...filterActiveStyle('totalExpenditure'), justifyContent: 'center' }}
          body={formatTotalExpenditure}
          dataType="numeric"
          style={{ minWidth: '8rem', textAlign: 'right' }}
          filter
          filterField="_totalExpenditure"
          filterElement={totalExpenditureFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, totalExpenditure: { min: null, max: null } };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
        />
        <Column
          header={t('projectProposal.viewList.startDate')}
          style={{ minWidth: '8rem' }}
          body={formatStartDate}
          dataType="date"
        />
        <Column
          header={t('projectProposal.viewList.endDate')}
          style={{ minWidth: '8rem' }}
          body={formatEndDate}
          dataType="date"
        />
        <Column
          header={t('projectProposal.viewList.status')}
          body={formatStatus}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        pageName="projects-revise"
        showLeftContent={false}
      />

      <ErrorDialog
        title={t('projectProposal.exportExcelErrorTitle')}
        isError={isExportExcelError}
        error={exportExcelError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
    </div>
  );
  // #endregion Render
}

export default CompletedProjectProposalViewList;
